import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
export type LegalDoc = 'privacy' | 'terms';
interface LegalModalProps {
  doc: LegalDoc | null;
  onClose: () => void;
}
const updated = 'June 15, 2026';
const content: Record<LegalDoc, {
  title: string;
  tag: string;
  sections: {
    heading: string;
    body: string[];
  }[];
}> = {
  privacy: {
    title: 'Privacy Policy',
    tag: 'privacy.md',
    sections: [{
      heading: '1. Overview',
      body: ['This Privacy Policy explains how Alex Circei ("I", "me") collect, use, and protect information when you visit this site or engage my advisory services. I take a minimal-data approach: I only collect what is necessary to respond to you and deliver engagements.']
    }, {
      heading: '2. Information I Collect',
      body: ['Contact details you provide voluntarily — such as your name, email address, company, and any context you share when booking a call or emailing me directly.', 'Basic, aggregated analytics about site usage (pages visited, approximate region). This is never used to identify you individually.']
    }, {
      heading: '3. How I Use It',
      body: ['To respond to inquiries, schedule and conduct advisory calls, and deliver agreed-upon engagements.', 'To improve the content and structure of this site.', 'I do not sell, rent, or trade your personal information to third parties.']
    }, {
      heading: '4. Third-Party Services',
      body: ['Scheduling is handled via Calendly and correspondence via email (Gmail). These providers process data under their own privacy policies. No advisory content is shared with advertisers.']
    }, {
      heading: '5. Data Retention & Your Rights',
      body: ['I retain correspondence only as long as needed for our working relationship or as required by law. You may request access to, correction of, or deletion of your information at any time by emailing alex.circei@gmail.com.']
    }, {
      heading: '6. Contact',
      body: ['Questions about this policy? Reach me at alex.circei@gmail.com.']
    }]
  },
  terms: {
    title: 'Terms of Service',
    tag: 'terms.md',
    sections: [{
      heading: '1. Acceptance of Terms',
      body: ['By accessing this site or engaging my advisory services, you agree to these Terms of Service. If you do not agree, please do not use the site or services.']
    }, {
      heading: '2. Nature of Services',
      body: ['I provide strategic advisory and consulting on engineering leadership, developer productivity, market positioning, fundraising, and related topics. Advice is provided on a best-effort, good-faith basis and is informational in nature.', 'No specific outcome (funding raised, analyst placement, hire, or metric improvement) is guaranteed. Results depend on factors outside my control.']
    }, {
      heading: '3. Engagements & Fees',
      body: ['Scope, deliverables, timelines, and fees for paid engagements are agreed separately in writing before work begins. An introductory call carries no obligation.']
    }, {
      heading: '4. Confidentiality',
      body: ['Information shared during engagements is treated as confidential and is not disclosed to third parties without consent, except where required by law. A mutual NDA can be put in place on request.']
    }, {
      heading: '5. Intellectual Property',
      body: ['Frameworks, templates, and materials I provide remain my intellectual property and are licensed to you for internal use within your organization.']
    }, {
      heading: '6. Limitation of Liability',
      body: ['To the maximum extent permitted by law, my liability arising from any engagement is limited to the fees paid for that engagement. I am not liable for indirect or consequential damages.']
    }, {
      heading: '7. Contact',
      body: ['Questions about these terms? Reach me at alex.circei@gmail.com.']
    }]
  }
};
export function LegalModal({
  doc,
  onClose
}: LegalModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (doc) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [doc, onClose]);
  const data = doc ? content[doc] : null;
  return <AnimatePresence data-id="element-210">
      {doc && data && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} data-id="element-211">
          <div className="absolute inset-0 bg-base/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-212" />

          <motion.div role="dialog" aria-modal="true" aria-label={data.title} initial={{
        opacity: 0,
        y: 16,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 16,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} className="relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-xl border border-border bg-surface overflow-hidden shadow-2xl" data-id="element-213">
            {/* terminal window chrome */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-base/50" data-id="element-214">
              <div className="flex items-center gap-3" data-id="element-215">
                <div className="flex gap-1.5" data-id="element-216">
                  <span className="w-3 h-3 rounded-full bg-muted/40" data-id="element-217" />
                  <span className="w-3 h-3 rounded-full bg-muted/40" data-id="element-218" />
                  <span className="w-3 h-3 rounded-full bg-accent/60" data-id="element-219" />
                </div>
                <span className="font-mono text-xs text-muted" data-id="element-220">
                  ~/legal/{data.tag}
                </span>
              </div>
              <button onClick={onClose} aria-label="Close" className="text-muted hover:text-accent transition-colors" data-id="element-221">
                <X className="w-5 h-5" data-id="element-222" />
              </button>
            </div>

            {/* body */}
            <div className="overflow-y-auto px-6 md:px-8 py-8" data-id="element-223">
              <p className="font-mono text-[10px] uppercase tracking-widest text-accent mb-2" data-id="element-224">
                {'//'} Last updated {updated}
              </p>
              <h2 className="text-2xl md:text-3xl font-serif tracking-tight mb-8" data-id="element-225">
                {data.title}
              </h2>

              <div className="space-y-7" data-id="element-226">
                {data.sections.map(s => <div key={s.heading} data-id="element-227">
                    <h3 className="font-mono text-sm text-primary mb-2 tracking-tight" data-id="element-228">
                      {s.heading}
                    </h3>
                    <div className="space-y-3" data-id="element-229">
                      {s.body.map((p, i) => <p key={i} className="text-sm text-secondary leading-relaxed" data-id="element-230">
                          {p}
                        </p>)}
                    </div>
                  </div>)}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}